// src/components/auth/AuthSuccess.tsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import styled from "styled-components"; // We now use styled-components for styling
import { useUser } from "../../contexts/UserContext";
import { useAuth } from "../../contexts/AuthContext";
import { formatSchoolId } from "../../utils/formatUtils";

// Styled Components
const Container = styled.div`
  max-width: 600px;
  margin: 30px auto;
  padding: 30px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const Title = styled.h2`
  color: #7e22ce; /* Primary app color */
  margin-bottom: 20px;
`;

const InfoText = styled.p`
  font-size: 1rem;
  color: #333;
  margin: 10px 0;
`;

const ErrorText = styled.p`
  font-size: 1rem;
  color: #ef4444;
  margin: 10px 0;
`;

const FinishButton = styled.button`
  margin-top: 20px;
  padding: 12px 30px;
  background-color: #7e22ce;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #5a17a7;
  }
`;

interface AuthSuccessProps {
  onFinish: () => Promise<void>;
}

const AuthSuccess: React.FC<AuthSuccessProps> = ({ onFinish }) => {
  const { profile, setProfile } = useUser();
  const { token } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!token || profile) return; // Don't fetch if we already have a profile
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || "https://lavernaibackend.onrender.com/api"}/users/me`,
          {
            headers: { "x-auth-token": token },
          }
        );
        // Transform the fetched user so that it includes the 'id' property (using _id from backend)
        const fetchedUser = { ...response.data.data.user, id: response.data.data.user._id };
        setProfile(fetchedUser);
      } catch (err: any) {
        setError(err.response?.data?.message || "Error fetching user profile");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [token, setProfile, profile]); // Add profile to dependencies

  return (
    <Container>
      <Title>Authentication Successful</Title>
      {loading ? (
        <InfoText>Loading user information...</InfoText>
      ) : error ? (
        <ErrorText>{error}</ErrorText>
      ) : profile ? (
        <>
          <InfoText>
            <strong>Name:</strong> {`${profile.firstName} ${profile.lastName}`}
          </InfoText>
          <InfoText>
            <strong>Grade:</strong> {profile.grade}
          </InfoText>
          <InfoText>
            <strong>School ID:</strong> {profile.schoolId}
          </InfoText>
          {profile.strictModeActivated && (
            <InfoText className="strict-mode-notice">LavernAI Strict Mode Activated</InfoText>
          )}
          <InfoText>Welcome, {`${profile.firstName} ${profile.lastName}`}. You will enjoy studying with us.</InfoText>
          {/* Button to finish the onboarding process */}
          <FinishButton onClick={onFinish}>Finish Onboarding</FinishButton>
        </>
      ) : (
        <InfoText>No user information available.</InfoText>
      )}
    </Container>
  );
};

export default AuthSuccess;
